import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Car, Plus, Trash2, Zap, ChevronRight, Fuel, Cpu, Hash, History, Usb
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function AddVehicleForm({ onAdd, onCancel }) {
  const [form, setForm] = useState({ year: '', make: '', model: '', engine: '', ecu_type: '', vin: '', fuel_type: '' });
  const [submitting, setSubmitting] = useState(false);
  const update = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onAdd({ ...form, year: parseInt(form.year) || 2020 });
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Add vehicle failed:', err);
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3 p-4 bg-[#0A0A0A] border border-[#27272A] rounded-sm" data-testid="add-vehicle-form">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <input required placeholder="Year" value={form.year} onChange={update('year')} data-testid="vehicle-year"
          className="bg-[#050505] border border-[#27272A] text-white rounded-sm px-3 py-2 text-sm focus:border-[#FF5E1A] outline-none" />
        <input required placeholder="Make" value={form.make} onChange={update('make')} data-testid="vehicle-make"
          className="bg-[#050505] border border-[#27272A] text-white rounded-sm px-3 py-2 text-sm focus:border-[#FF5E1A] outline-none" />
        <input required placeholder="Model" value={form.model} onChange={update('model')} data-testid="vehicle-model"
          className="bg-[#050505] border border-[#27272A] text-white rounded-sm px-3 py-2 text-sm focus:border-[#FF5E1A] outline-none" />
        <input placeholder="Engine (e.g. N51)" value={form.engine} onChange={update('engine')}
          className="bg-[#050505] border border-[#27272A] text-white rounded-sm px-3 py-2 text-sm focus:border-[#FF5E1A] outline-none" />
        <input placeholder="ECU Type (e.g. MSV80)" value={form.ecu_type} onChange={update('ecu_type')}
          className="bg-[#050505] border border-[#27272A] text-white rounded-sm px-3 py-2 text-sm focus:border-[#FF5E1A] outline-none" />
        <input placeholder="Fuel (91, 93, E85)" value={form.fuel_type} onChange={update('fuel_type')}
          className="bg-[#050505] border border-[#27272A] text-white rounded-sm px-3 py-2 text-sm focus:border-[#FF5E1A] outline-none" />
      </div>
      <input placeholder="VIN (optional)" value={form.vin} onChange={update('vin')}
        className="w-full bg-[#050505] border border-[#27272A] text-white rounded-sm px-3 py-2 text-sm focus:border-[#FF5E1A] outline-none" />
      <div className="flex gap-2">
        <Button type="submit" disabled={submitting} data-testid="vehicle-submit"
          className="bg-[#FF5E1A] hover:bg-[#FF7A42] text-white rounded-sm text-xs font-bold uppercase">
          {submitting ? 'Adding...' : 'Add Vehicle'}
        </Button>
        <Button type="button" onClick={onCancel} variant="outline" className="border-zinc-700 text-zinc-400 rounded-sm text-xs">Cancel</Button>
      </div>
    </form>
  );
}

export default function CustomerDashboard({ onSelectVehicle }) {
  const { user, addVehicle, removeVehicle, refreshUser } = useAuth();
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeVehicle, setActiveVehicle] = useState(null);
  const [flashHistory, setFlashHistory] = useState([]);
  const [savingState, setSavingState] = useState({});
  const [availableTunes, setAvailableTunes] = useState([]); // [{filename, version, sha256, created_at, ...}]
  const [tunesLoading, setTunesLoading] = useState(true);
  const [tunesError, setTunesError] = useState(null);
  const [tunesRefreshTick, setTunesRefreshTick] = useState(0);
  const [pendingTune, setPendingTune] = useState({}); // {vehicleId: pendingFilename}

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('auth_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API_URL}/api/flash/history`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(setFlashHistory)
      .catch(err => {
        if (process.env.NODE_ENV === 'development') console.error('Flash history load error:', err);
      });
  }, [user]);

  // Separate effect for the tunes list — has its own loading/error/retry state
  // because on production the /api/bin/files endpoint can take ~5s (reads and
  // byte-diffs every 2MB bin against V9). Without a proper loading state the
  // Garage tune dropdown appears empty intermittently while the fetch is in
  // flight or if the network hiccups. AbortController prevents race conditions
  // when the effect re-fires (e.g. after refreshUser mutates the user ref).
  useEffect(() => {
    if (!user) return;
    const controller = new AbortController();
    let cancelled = false;
    setTunesLoading(true);
    setTunesError(null);
    // 30s hard cap — matches typical browser fetch timeout expectations and
    // is well above the observed ~5s worst-case backend response
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    fetch(`${API_URL}/api/bin/files`, { signal: controller.signal })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((files) => {
        if (cancelled) return;
        const seen = new Set();
        const tunes = (files || []).filter(f =>
          /\.bin$/i.test(f.filename) &&
          (f.filename.includes('Stage2') || f.filename.includes('E85') || f.filename.includes('stock') || f.version)
        ).filter(f => (seen.has(f.filename) ? false : seen.add(f.filename)));
        setAvailableTunes(tunes);
        setTunesLoading(false);
      })
      .catch(err => {
        if (cancelled || err.name === 'AbortError') return;
        if (process.env.NODE_ENV === 'development') console.error('Tunes list load error:', err);
        setTunesError(err.message || 'Failed to load tunes');
        setTunesLoading(false);
      })
      .finally(() => clearTimeout(timeoutId));

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [user, tunesRefreshTick]);

  const reloadTunes = () => setTunesRefreshTick(t => t + 1);

  // Per-vehicle inline feedback for the Lock In / fuel-change buttons. Values:
  // {type: 'success'|'error', msg: string} — auto-clears 3s after display.
  const [saveFeedback, setSaveFeedback] = useState({});

  const showFeedback = (vehicleId, type, msg) => {
    setSaveFeedback(s => ({ ...s, [vehicleId]: { type, msg } }));
    setTimeout(() => {
      setSaveFeedback(s => {
        const next = { ...s };
        delete next[vehicleId];
        return next;
      });
    }, 3200);
  };

  const updateVehicleState = async (vehicleId, patch) => {
    setSavingState(s => ({ ...s, [vehicleId]: true }));
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/api/auth/vehicles/${vehicleId}/state`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        let detail = `Save failed (HTTP ${res.status})`;
        try { const j = await res.json(); if (j.detail) detail = j.detail; } catch (_) { /* ignore */ }
        throw new Error(detail);
      }
      if (refreshUser) await refreshUser();
      // Tell the user what stuck — otherwise the UX feels unresponsive because
      // the change reflects only in the small "locked:" tag next to the label.
      if ('current_tune_version' in patch) {
        const tuneShort = (patch.current_tune_version || 'stock').split('_').slice(-1)[0].replace(/\.bin$/, '') || 'stock';
        showFeedback(vehicleId, 'success', `Locked in ${tuneShort}`);
      } else if ('current_fuel' in patch) {
        showFeedback(vehicleId, 'success', `Fuel saved: ${patch.current_fuel || 'unknown'}`);
      } else {
        showFeedback(vehicleId, 'success', 'Saved');
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Vehicle state update failed:', err);
      showFeedback(vehicleId, 'error', err.message || 'Save failed — please try again');
    } finally {
      setSavingState(s => ({ ...s, [vehicleId]: false }));
    }
  };

  // Auto-dedupe: run once per session per user. If the current user has
  // duplicate vehicles (same year/make/model/engine/vin), merge them silently
  // so the Garage doesn't show 4 identical BMW cards. Backfill fix for users
  // who accumulated dupes before the POST /vehicles idempotency check landed.
  useEffect(() => {
    if (!user) return;
    if (!Array.isArray(user.vehicles) || user.vehicles.length < 2) return;
    // Detect: any pair with same (year+make+model+engine+vin) tuple?
    const seen = new Set();
    let dupsFound = false;
    for (const v of user.vehicles) {
      const key = [
        v.year,
        (v.make || '').toLowerCase(),
        (v.model || '').toLowerCase(),
        (v.engine || '').toLowerCase(),
        (v.vin || '').toUpperCase(),
      ].join('|');
      if (seen.has(key)) { dupsFound = true; break; }
      seen.add(key);
    }
    if (!dupsFound) return;

    const token = localStorage.getItem('auth_token');
    fetch(`${API_URL}/api/auth/vehicles/deduplicate`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : null)
      .then((data) => {
        if (data?.removed > 0 && refreshUser) refreshUser();
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') console.warn('vehicle dedupe failed', err);
      });
    // Run only when the vehicles array changes length — not on every render.
  }, [user?.user_id, user?.vehicles?.length]);

  if (!user) return null;

  const vehicles = user.vehicles || [];

  const handleAdd = async (data) => {
    await addVehicle(data);
    setShowAddForm(false);
  };

  const handleSelect = (v) => {
    setActiveVehicle(v.vehicle_id);
    if (onSelectVehicle) onSelectVehicle(v);
  };

  return (
    <div className="space-y-6" data-testid="customer-dashboard">
      {/* Welcome + Credits */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white font-['Outfit']">Welcome back, {user.name?.split(' ')[0]}</h2>
          <p className="text-sm text-zinc-500">Manage your garage, credits, and tunes</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-[#111111] border border-[#27272A] px-4 py-2.5 rounded-sm" data-testid="credits-display">
            <Zap className="w-4 h-4 text-[#FF5E1A]" />
            <span className="text-lg font-bold text-white font-['JetBrains_Mono']">{user.credits}</span>
            <span className="text-xs text-zinc-500">credits</span>
          </div>
        </div>
      </div>

      {/* Garage */}
      <Card className="bg-[#111111] border-[#27272A] rounded-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-white font-['Outfit'] text-base">
              <Car className="w-4 h-4 text-[#FF5E1A]" />My Garage
              <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-xs ml-2">{vehicles.length}</Badge>
            </CardTitle>
            <Button onClick={() => setShowAddForm(!showAddForm)} size="sm" data-testid="add-vehicle-btn"
              className="bg-[#FF5E1A] hover:bg-[#FF7A42] text-white rounded-sm text-xs font-bold">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Vehicle
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {showAddForm && <AddVehicleForm onAdd={handleAdd} onCancel={() => setShowAddForm(false)} />}

          {vehicles.length === 0 && !showAddForm ? (
            <div className="text-center py-10">
              <Car className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">No vehicles yet. Add your first car to get started.</p>
              <Button onClick={() => setShowAddForm(true)} size="sm" className="mt-3 bg-[#FF5E1A] hover:bg-[#FF7A42] text-white rounded-sm text-xs">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Vehicle
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {vehicles.map((v) => (
                <div key={v.vehicle_id}
                  onClick={() => handleSelect(v)}
                  data-testid={`vehicle-card-${v.vehicle_id}`}
                  className={`p-4 border rounded-sm cursor-pointer transition-all group ${
                    activeVehicle === v.vehicle_id
                      ? 'border-[#FF5E1A]/50 bg-[#FF5E1A]/5'
                      : 'border-[#27272A] bg-[#050505] hover:border-zinc-600'
                  }`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-white font-bold text-sm font-['Outfit']">
                        {v.year} {v.make} {v.model}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {v.engine && (
                          <span className="flex items-center gap-1 text-xs text-zinc-400">
                            <Cpu className="w-3 h-3" />{v.engine}
                          </span>
                        )}
                        {v.ecu_type && (
                          <span className="flex items-center gap-1 text-xs text-cyan-400">
                            <Hash className="w-3 h-3" />{v.ecu_type}
                          </span>
                        )}
                        {v.fuel_type && (
                          <span className="flex items-center gap-1 text-xs text-emerald-400">
                            <Fuel className="w-3 h-3" />{v.fuel_type}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {activeVehicle === v.vehicle_id && (
                        <Badge className="bg-[#FF5E1A]/20 text-[#FF5E1A] text-[9px] rounded-sm">ACTIVE</Badge>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); removeVehicle(v.vehicle_id); }}
                        className="text-zinc-700 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                        data-testid={`remove-vehicle-${v.vehicle_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#27272A]">
                    <span className="text-xs text-zinc-600">Click to select for tuning</span>
                    <ChevronRight className={`w-4 h-4 transition-colors ${activeVehicle === v.vehicle_id ? 'text-[#FF5E1A]' : 'text-zinc-700'}`} />
                  </div>

                  {/* AI-awareness state: what's actually in the car RIGHT NOW.
                      The AI reads these on every chat so it always knows the
                      tune/fuel combo and warns on dangerous mismatches. */}
                  <div
                    className="mt-3 pt-3 border-t border-[#27272A] grid grid-cols-2 gap-2"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`vehicle-state-${v.vehicle_id}`}
                  >
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-['JetBrains_Mono'] flex flex-col gap-1">
                      Fuel In Tank
                      <select
                        value={v.current_fuel || ''}
                        onChange={(e) => updateVehicleState(v.vehicle_id, { current_fuel: e.target.value })}
                        disabled={!!savingState[v.vehicle_id]}
                        className="bg-[#050505] border border-[#27272A] text-white text-xs px-2 py-1.5 rounded-sm focus:border-[#FF5E1A] focus:outline-none disabled:opacity-50"
                        data-testid={`fuel-select-${v.vehicle_id}`}
                      >
                        <option value="">— Unknown —</option>
                        <option value="E85">E85</option>
                        <option value="91">91 Octane</option>
                        <option value="93">93 Octane</option>
                        <option value="87">87 Octane</option>
                        <option value="MIXED_E85_91">Mixed E85 + 91</option>
                        <option value="MIXED_E85_93">Mixed E85 + 93</option>
                      </select>
                    </label>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-['JetBrains_Mono'] flex flex-col gap-1 col-span-2">
                      <div className="flex items-center justify-between">
                        <span>Currently Flashed{v.current_tune_version ? <span className="text-emerald-400 normal-case tracking-normal ml-2">· locked: {v.current_tune_version.replace(/^BMW_.+?_(?:Stage2_)?(?:E85_)?/, '').replace(/\.bin$/, '')}</span> : ''}</span>
                        <div className="flex items-center gap-2">
                          {tunesLoading && !tunesError && (
                            <span className="text-zinc-500 normal-case tracking-normal text-[10px]" data-testid={`tunes-loading-${v.vehicle_id}`}>loading tunes…</span>
                          )}
                          {tunesError && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); reloadTunes(); }}
                              className="text-red-400 hover:text-red-300 normal-case tracking-normal text-[10px] underline"
                              data-testid={`tunes-retry-${v.vehicle_id}`}
                              title={tunesError}
                            >
                              load failed — retry
                            </button>
                          )}
                          {!tunesLoading && !tunesError && availableTunes.length > 0 && (
                            <span className="text-zinc-600 normal-case tracking-normal text-[10px]">{availableTunes.length} tunes</span>
                          )}
                          {pendingTune[v.vehicle_id] !== undefined && pendingTune[v.vehicle_id] !== (v.current_tune_version || '') && (
                            <span className="text-amber-400 normal-case tracking-normal text-[10px]">unsaved selection</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <select
                          value={pendingTune[v.vehicle_id] !== undefined ? pendingTune[v.vehicle_id] : (v.current_tune_version || '')}
                          onChange={(e) => setPendingTune(p => ({ ...p, [v.vehicle_id]: e.target.value }))}
                          disabled={!!savingState[v.vehicle_id]}
                          className="flex-1 bg-[#050505] border border-[#27272A] text-white text-xs px-2 py-1.5 rounded-sm focus:border-[#FF5E1A] focus:outline-none disabled:opacity-50"
                          data-testid={`tune-select-${v.vehicle_id}`}
                        >
                          <option value="">— None / Stock —</option>
                          {tunesLoading && availableTunes.length === 0 && (
                            <option value="" disabled>Loading available tunes…</option>
                          )}
                          {tunesError && availableTunes.length === 0 && (
                            <option value="" disabled>Failed to load tunes — click retry above</option>
                          )}
                          {availableTunes.map(t => {
                            const status = t.run_status && t.run_status !== 'untested' ? ` [${t.run_status}]` : '';
                            const aiTag = t.is_ai_generated ? ' · AI' : '';
                            const flashTag = t.flash_mode === 'full' ? ' · ⚠ FULL' : (t.flash_mode === 'short' ? ' · ⚡ SHORT' : '');
                            const label = t.version
                              ? `${t.version}${aiTag}${flashTag}${t.sha256 ? ` · sha:${String(t.sha256).slice(0,8)}` : ''}${t.created_at ? ` · ${new Date(t.created_at).toLocaleDateString()}` : ''}${status}`
                              : `${t.filename}${aiTag}${flashTag}${status}`;
                            return (
                              <option key={t.filename} value={t.filename} title={t.filename}>
                                {label}
                              </option>
                            );
                          })}
                          {v.current_tune_version && !availableTunes.some(t => t.filename === v.current_tune_version) && (
                            <option value={v.current_tune_version}>{v.current_tune_version} (custom)</option>
                          )}
                        </select>
                        <button
                          type="button"
                          disabled={!!savingState[v.vehicle_id] || pendingTune[v.vehicle_id] === undefined || pendingTune[v.vehicle_id] === (v.current_tune_version || '')}
                          onClick={async () => {
                            const newTune = pendingTune[v.vehicle_id] || '';
                            await updateVehicleState(v.vehicle_id, { current_tune_version: newTune });
                            setPendingTune(p => { const n = { ...p }; delete n[v.vehicle_id]; return n; });
                          }}
                          className="px-3 py-1.5 bg-[#FF5E1A] hover:bg-[#FF7A3A] disabled:bg-zinc-700 disabled:text-zinc-500 disabled:cursor-not-allowed text-black font-bold text-xs rounded-sm transition-colors uppercase tracking-wider"
                          data-testid={`lock-in-tune-${v.vehicle_id}`}
                          title="Lock in this tune as currently flashed. Datalogs uploaded after this point will be tagged with it."
                        >
                          {savingState[v.vehicle_id] ? '…' : 'Lock In'}
                        </button>
                      </div>
                      {saveFeedback[v.vehicle_id] && (
                        <div
                          className={`text-[10px] font-bold uppercase tracking-wider ${
                            saveFeedback[v.vehicle_id].type === 'success'
                              ? 'text-emerald-400'
                              : 'text-red-400'
                          }`}
                          data-testid={`lock-in-feedback-${v.vehicle_id}`}
                          role="status"
                        >
                          {saveFeedback[v.vehicle_id].type === 'success' ? '✓ ' : '⚠ '}
                          {saveFeedback[v.vehicle_id].msg}
                        </div>
                      )}
                      <span className="text-[10px] normal-case tracking-normal text-zinc-500">
                        Datalogs you upload after locking in will be auto-tagged with this tune for per-version stats.
                      </span>
                      {/* Tune details panel — notes, run status, delete */}
                      {(() => {
                        const selectedFn = pendingTune[v.vehicle_id] !== undefined ? pendingTune[v.vehicle_id] : (v.current_tune_version || '');
                        if (!selectedFn) return null;
                        const meta = availableTunes.find(t => t.filename === selectedFn) || {};
                        const statusColors = {
                          works: 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300',
                          rough: 'bg-amber-500/20 border-amber-500/40 text-amber-300',
                          no_run: 'bg-orange-500/20 border-orange-500/40 text-orange-300',
                          crashed_ecu: 'bg-red-500/20 border-red-500/40 text-red-300',
                          untested: 'bg-zinc-700/40 border-zinc-600/40 text-zinc-400',
                        };
                        const status = meta.run_status || 'untested';
                        const statusLabels = { works: '✓ Works', rough: '~ Rough', no_run: '✗ Won\u0027t Run', crashed_ecu: '⚠ Crashed ECU', untested: '? Untested' };
                        const isAdmin = user?.role === 'admin';
                        return (
                          <div className="mt-2 p-2.5 bg-[#050505] border border-[#1f1f1f] rounded-sm space-y-2 normal-case tracking-normal" data-testid={`tune-details-${v.vehicle_id}`}>
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-[10px] px-2 py-0.5 border rounded-sm font-['JetBrains_Mono'] uppercase tracking-wider ${statusColors[status]}`}>
                                  {statusLabels[status]}
                                </span>
                                {meta.is_ai_generated && (
                                  <span className="text-[10px] px-2 py-0.5 bg-fuchsia-500/20 border border-fuchsia-500/40 text-fuchsia-300 rounded-sm font-['JetBrains_Mono'] uppercase tracking-wider">AI Generated</span>
                                )}
                                {meta.flash_mode === 'full' && (
                                  <span
                                    className="text-[10px] px-2 py-0.5 bg-red-500/20 border border-red-500/40 text-red-300 rounded-sm font-['JetBrains_Mono'] uppercase tracking-wider"
                                    title={`Outside cal block: ${meta.outside_cal_bytes_changed || 0} bytes changed. FULL FLASH REQUIRED — cannot use Flash Tune / short flash. Flash group: ${meta.flash_group_id}`}
                                    data-testid="tune-flash-full-badge"
                                  >
                                    ⚠ FULL FLASH
                                  </span>
                                )}
                                {meta.flash_mode === 'short' && (
                                  <span
                                    className="text-[10px] px-2 py-0.5 bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 rounded-sm font-['JetBrains_Mono'] uppercase tracking-wider"
                                    title={`Cal block only — ${meta.inside_cal_bytes_changed || 0} bytes changed inside 0x040000-0x053DFF, 0 bytes outside. Can be applied via short Flash Tune from any tune in flash group ${meta.flash_group_id}`}
                                    data-testid="tune-flash-short-badge"
                                  >
                                    ⚡ SHORT FLASH
                                  </span>
                                )}
                                {meta.flash_group_id && meta.flash_group_id !== 'v9_canonical' && meta.flash_group_id !== 'size_mismatch' && (
                                  <span
                                    className="text-[10px] px-2 py-0.5 bg-zinc-700/40 border border-zinc-600/40 text-zinc-300 rounded-sm font-['JetBrains_Mono'] uppercase tracking-wider"
                                    title={`Flash group ${meta.flash_group_id}. Tunes sharing this group can short-flash between each other (only cal block needs to change). Going to a tune in a DIFFERENT group requires a full flash.`}
                                  >
                                    FG:{String(meta.flash_group_id).slice(0, 6)}
                                  </span>
                                )}
                                {meta.sha256 && (
                                  <span className="text-[10px] text-zinc-500 font-['JetBrains_Mono']">sha:{String(meta.sha256).slice(0,8)}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  title="Edit notes + run status"
                                  onClick={async () => {
                                    const curNotes = meta.user_notes || '';
                                    const newNotes = window.prompt(`Notes for ${selectedFn.replace(/^BMW_.+?_(?:Stage2_)?(?:E85_)?/, '').replace(/\.bin$/, '')}\n\nLog idle behavior, drivability, any CEL/limp issues. Used by AI for context on next iteration.`, curNotes);
                                    if (newNotes === null) return;
                                    const statusChoice = window.prompt('Run status?\n\n1 = works\n2 = rough (drives but rough)\n3 = no_run (won\u0027t start or stalls)\n4 = crashed_ecu (bricked / limp mode)\n5 = untested', '1');
                                    const map = { '1': 'works', '2': 'rough', '3': 'no_run', '4': 'crashed_ecu', '5': 'untested' };
                                    const newStatus = map[String(statusChoice || '').trim()] || meta.run_status || 'untested';
                                    try {
                                      const token = localStorage.getItem('auth_token');
                                      const res = await fetch(`${API_URL}/api/tunes/${encodeURIComponent(selectedFn)}/notes`, {
                                        method: 'PATCH',
                                        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                                        body: JSON.stringify({ user_notes: newNotes, run_status: newStatus }),
                                      });
                                      if (!res.ok) { const d = await res.json().catch(() => ({})); window.alert(`Save failed: ${d.detail || res.statusText}`); return; }
                                      // Reload tunes list to pick up new notes
                                      const fr = await fetch(`${API_URL}/api/bin/files`);
                                      if (fr.ok) {
                                        const files = await fr.json();
                                        const filtered = (files || []).filter(f => /\.bin$/i.test(f.filename) && (f.filename.includes('Stage2') || f.filename.includes('E85') || f.filename.includes('stock') || f.version));
                                        setAvailableTunes(filtered);
                                      }
                                    } catch (err) { window.alert(`Save failed: ${err.message}`); }
                                  }}
                                  className="text-[10px] px-2 py-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-sm uppercase tracking-wider font-['JetBrains_Mono']"
                                  data-testid={`edit-tune-notes-${v.vehicle_id}`}
                                >
                                  Edit Notes
                                </button>
                                {isAdmin && (
                                  <button
                                    type="button"
                                    title="Delete this tune file permanently (disk + GridFS + DB row)"
                                    onClick={async () => {
                                      if (!window.confirm(`DELETE ${selectedFn}?\n\nThis removes the bin from disk, GridFS, and the DB. Cannot be undone.`)) return;
                                      try {
                                        const token = localStorage.getItem('auth_token');
                                        const res = await fetch(`${API_URL}/api/tunes/${encodeURIComponent(selectedFn)}`, {
                                          method: 'DELETE',
                                          headers: token ? { Authorization: `Bearer ${token}` } : {},
                                        });
                                        if (!res.ok) { const d = await res.json().catch(() => ({})); window.alert(`Delete failed: ${d.detail || res.statusText}`); return; }
                                        const d = await res.json();
                                        window.alert(`Deleted ${d.filename}\nDB rows removed: ${d.db_rows}`);
                                        // If it was the locked tune, clear it
                                        if (v.current_tune_version === selectedFn) {
                                          await updateVehicleState(v.vehicle_id, { current_tune_version: '' });
                                        }
                                        setPendingTune(p => { const n = { ...p }; delete n[v.vehicle_id]; return n; });
                                        // Reload tunes
                                        const fr = await fetch(`${API_URL}/api/bin/files`);
                                        if (fr.ok) {
                                          const files = await fr.json();
                                          const filtered = (files || []).filter(f => /\.bin$/i.test(f.filename) && (f.filename.includes('Stage2') || f.filename.includes('E85') || f.filename.includes('stock') || f.version));
                                          setAvailableTunes(filtered);
                                        }
                                      } catch (err) { window.alert(`Delete failed: ${err.message}`); }
                                    }}
                                    className="text-[10px] px-2 py-1 bg-red-500/20 hover:bg-red-500/30 border border-red-500/40 text-red-300 rounded-sm uppercase tracking-wider font-['JetBrains_Mono']"
                                    data-testid={`delete-tune-${v.vehicle_id}`}
                                  >
                                    Delete
                                  </button>
                                )}
                              </div>
                            </div>
                            {meta.user_notes && (
                              <div className="text-[11px] text-zinc-300 italic border-l-2 border-zinc-700 pl-2">
                                &ldquo;{meta.user_notes}&rdquo;
                              </div>
                            )}
                            {!meta.user_notes && (
                              <div className="text-[10px] text-zinc-600 italic">No notes yet. Click &ldquo;Edit Notes&rdquo; to log drivability, idle behavior, CEL/limp events, or to flag this tune as not-runnable.</div>
                            )}
                            {meta.flash_group_id && meta.flash_group_id !== 'v9_canonical' && meta.flash_group_id !== 'size_mismatch' && (() => {
                              const peers = availableTunes.filter(t => t.flash_group_id === meta.flash_group_id && t.filename !== selectedFn);
                              if (peers.length === 0) return null;
                              return (
                                <div className="text-[10px] text-cyan-400/80 border-l-2 border-cyan-500/30 pl-2" data-testid="tune-flash-peers">
                                  ⚡ <strong>{peers.length}</strong> other tune{peers.length === 1 ? '' : 's'} can short-flash {meta.flash_mode === 'full' ? 'WITHIN this full-flash group' : 'with this one'}:{' '}
                                  <span className="text-zinc-400">
                                    {peers.slice(0, 4).map(p => p.version || p.filename.replace(/^BMW_.+?_(?:Stage2_)?(?:E85_)?/, '').replace(/\.bin$/, '').slice(0, 24)).join(', ')}
                                    {peers.length > 4 && `, +${peers.length - 4} more`}
                                  </span>
                                </div>
                              );
                            })()}
                            {meta.flash_mode === 'full' && (
                              <div className="text-[10px] text-red-300/80 border-l-2 border-red-500/30 pl-2">
                                🔴 <strong>Full flash required</strong> — {meta.outside_cal_bytes_changed || 0} bytes outside the cal block (0x040000-0x053DFF). Cannot use Flash Tune / short flash from a tune in a different flash group. {meta.inside_cal_bytes_changed > 0 && `Inside cal: ${meta.inside_cal_bytes_changed} bytes also changed.`}
                              </div>
                            )}
                            {meta.flash_mode === 'short' && (
                              <div className="text-[10px] text-emerald-300/80 border-l-2 border-emerald-500/30 pl-2">
                                🟢 <strong>Short flash compatible</strong> — only {meta.inside_cal_bytes_changed || 0} cal-block bytes changed. Can be applied via fast Flash Tune.
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </label>
                    {(v.current_tune_version || '').toUpperCase().match(/V\d+|E85/) &&
                     ['91', '93', '87'].includes((v.current_fuel || '').toUpperCase()) && (
                      <div className="col-span-2 mt-1 p-2 bg-red-500/10 border border-red-500/30 rounded-sm text-[11px] text-red-300 font-['JetBrains_Mono']">
                        ⚠ E85 tune running on pump gas — drive conservatively, avoid WOT until refueled with E85 or flashed back.
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Flash History */}
      <Card className="bg-[#111111] border-[#27272A] rounded-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white font-['Outfit'] text-base">
            <History className="w-4 h-4 text-cyan-400" />Flash History
            <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-xs ml-2">{flashHistory.length}</Badge>
          </CardTitle>
          <CardDescription className="text-zinc-500 text-xs">Track every tune version flashed to your ECU</CardDescription>
        </CardHeader>
        <CardContent>
          {flashHistory.length === 0 ? (
            <div className="text-center py-6">
              <Usb className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
              <p className="text-xs text-zinc-500">No flashes logged yet. Flash a tune to start tracking.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="flash-history-list">
              {flashHistory.map((f, i) => (
                <div key={f.flash_id || i} className="flex items-center gap-3 p-3 bg-[#050505] border border-[#27272A] rounded-sm" data-testid={`flash-entry-${i}`}>
                  <div className="w-8 h-8 bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center rounded-sm flex-shrink-0">
                    <Usb className="w-4 h-4 text-cyan-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-white font-medium font-['JetBrains_Mono'] truncate">{f.filename}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge className="bg-zinc-800 text-zinc-400 text-[9px] px-1.5 py-0 rounded-sm">{f.flash_type}</Badge>
                      {f.vehicle_info && <span className="text-[10px] text-zinc-600">{f.vehicle_info}</span>}
                    </div>
                  </div>
                  <span className="text-[10px] text-zinc-600 font-['JetBrains_Mono'] flex-shrink-0">
                    {f.created_at ? new Date(f.created_at).toLocaleDateString() : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
